import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';


const SecuritySection = () => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const activeSessions = [
    {
      id: 1,
      device: "Chrome on Windows",
      location: "New York, USA",
      ipAddress: "192.168.1.1",
      lastActive: "Active now",
      isCurrent: true
    },
    {
      id: 2,
      device: "Safari on iPhone",
      location: "New York, USA",
      ipAddress: "192.168.1.2",
      lastActive: "2 hours ago",
      isCurrent: false
    }
  ];

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!passwordData?.currentPassword) newErrors.currentPassword = "Current password is required";
    if (!passwordData?.newPassword) newErrors.newPassword = "New password is required";
    if (passwordData?.newPassword?.length < 8) newErrors.newPassword = "Password must be at least 8 characters";
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSavePassword = () => {
    if (validatePassword()) {
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const handleCancelPassword = () => {
    setIsChangingPassword(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setErrors({});
  };

  const handleEnable2FA = () => {
    setTwoFactorEnabled(true);
    setShowQRCode(true);
  };

  const handleDisable2FA = () => {
    setTwoFactorEnabled(false);
    setShowQRCode(false);
  };

  const handleTerminateSession = (sessionId) => {
    console.log('Terminating session:', sessionId);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} color="var(--color-error)" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">
              Account Security
            </h2>
            <p className="text-sm text-muted-foreground">
              Manage your password and security settings
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={20} color="var(--color-foreground)" />
              <div>
                <h3 className="text-sm font-medium text-foreground">Password</h3>
                <p className="text-sm text-muted-foreground">
                  Last changed 3 months ago
                </p>
              </div>
            </div>
            {!isChangingPassword && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsChangingPassword(true)}
              >
                Change
              </Button>
            )}
          </div>

          {isChangingPassword && (
            <div className="space-y-4 pt-4 border-t border-border">
              <Input
                label="Current Password"
                type="password"
                value={passwordData?.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
                error={errors?.currentPassword}
                required
              />
              <Input
                label="New Password"
                type="password"
                value={passwordData?.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
                error={errors?.newPassword}
                description="Must be at least 8 characters"
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={passwordData?.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
                error={errors?.confirmPassword}
                required
              />
              <div className="flex gap-3">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSavePassword}
                >
                  Update Password
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelPassword}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Icon name="Smartphone" size={20} color="var(--color-foreground)" />
              <div>
                <h3 className="text-sm font-medium text-foreground">
                  Two-Factor Authentication
                </h3>
                <p className="text-sm text-muted-foreground">
                  {twoFactorEnabled ? 'Enabled' : 'Add an extra layer of security'}
                </p>
              </div>
            </div>
            <Button
              variant={twoFactorEnabled ? "destructive" : "default"}
              size="sm"
              onClick={twoFactorEnabled ? handleDisable2FA : handleEnable2FA}
            >
              {twoFactorEnabled ? 'Disable' : 'Enable'}
            </Button>
          </div>

          {showQRCode && (
            <div className="pt-4 border-t border-border">
              <div className="bg-card p-4 rounded-lg border border-border text-center">
                <div className="w-48 h-48 bg-muted mx-auto mb-4 rounded-lg flex items-center justify-center">
                  <Icon name="QrCode" size={64} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Scan this QR code with your authenticator app
                </p>
                <Input
                  label="Verification Code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  className="max-w-xs mx-auto"
                />
                <Button variant="default" size="sm" className="mt-4">
                  Verify & Enable
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Monitor" size={20} color="var(--color-foreground)" />
            <div>
              <h3 className="text-sm font-medium text-foreground">Active Sessions</h3>
              <p className="text-sm text-muted-foreground">
                Manage devices where you're logged in
              </p>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-border">
            {activeSessions?.map((session) => (
              <div
                key={session?.id}
                className="flex items-start justify-between p-3 bg-card rounded-lg border border-border"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Monitor" size={20} color="var(--color-muted-foreground)" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-medium text-foreground">
                        {session?.device}
                      </h4>
                      {session?.isCurrent && (
                        <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {session?.location} • {session?.ipAddress}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {session?.lastActive}
                    </p>
                  </div>
                </div>
                {!session?.isCurrent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleTerminateSession(session?.id)}
                  >
                    Terminate
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;